import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Breadcrumb, TextInput, Title, Timeline, PopUpModal, SuccessPopupModal } from '../../Components/Atoms';
import { getLocalisedDate, getLocalisedTime } from '../../Library/datetime';
import { deleteBatteryFromInventory, getBatteriesData } from '../../APIs';
import { useNavigate, useParams } from 'react-router-dom';
import PATH from '../../Routes/paths';
import './style.css'
import { CONNECTOR_TYP_REV_MAP } from '../../Library/constants';
import { Button, useToast } from '@chakra-ui/react';
import { DeleteIcon, PenIcon } from '../../Assets/images/svgs';

const FIELD_TYPE = {
  TEXT: 1,
  AUTOCOMPLETE: 2,
};

const BatteryDetail = () => {
  const { bId } = useParams();
  const [formFields, setFormFields] = useState([]);
  const BatteryDetail = useRef({});
  const [timelineData, setTimelinedata] = useState([])
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmDeleteBattery, setConfirmDeleteBattery] = useState({});
  const Navigate = useNavigate();
  const Toast = useToast();

  const getFormFields = useCallback((formData = {}) => {
    const formFields = [
      {
        type: FIELD_TYPE.TEXT,
        label: "Battery UID",
        name: "bId",
        value: formData.bId
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Ampere Hours",
        name: "ampHrs",
        value: formData.ampHrs
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Voltage",
        name: "vltg",
        value: formData.vltg
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Manufacturer",
        name: "manf",
        value: formData.manf
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Connector Type",
        name: "cTyp",
        value: CONNECTOR_TYP_REV_MAP[formData.cTyp]
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Warranty Expiry",
        name: "exp",
        value: getLocalisedDate(formData.exp, true)
      },
      {
        type: FIELD_TYPE.TEXT,
        label: "Charging Cycles",
        name: "cCycl",
        value: formData.cCycl
      },
    ];

    if (formData.remCycl) {
      formFields.push({
        type: FIELD_TYPE.TEXT,
        label: "Remaining Cycles",
        name: "remCycl",
        value: formData.remCycl
      })
    }
    return formFields;
  }, []);

  useEffect(() => {
    const fetchBatteryDetail = async (bId) => {
      try{
        const data = await getBatteriesData(bId);
        if (data) {
          const batteryDetail = data[0];
          BatteryDetail.current = batteryDetail || {};

          let timelineDta = [];
          if (batteryDetail.swapHist && batteryDetail.swapHist.length)
            timelineDta = batteryDetail.swapHist.map(timeline => ({
              status: timeline.newBid === batteryDetail.bId,
              ts: timeline.ts,
              vehReg: timeline.reg
            }))
          setTimelinedata(timelineDta || []);
          setFormFields(getFormFields(batteryDetail));
        };
      }catch(err) {
        console.log(err);
      }
    };

    fetchBatteryDetail(bId);
  }, [getFormFields, bId])

  const DeleteBattery = (bId) => {
    deleteBatteryFromInventory(bId, (err) => {
      if (err) {
        console.error("BatteryDetail : Error while delete battery", err);
        setConfirmDeleteBattery({});
        Toast({ isClosable: true, description: "Failed to delete battery", position: "bottom", status: "error", variant: "solid" });
      }
      else {
        setShowSuccessModal(true);
      }
    })
  }

  const deleteBattery = () => {
    setConfirmDeleteBattery({
      open: true,
      modalMsg: "Are you sure you want to permanently remove this battery?",
      confirmBtn: "Remove",
      closeBtn: "Cancel",
      onConfirm: () => DeleteBattery(bId),
      onModalClose: () => setConfirmDeleteBattery({}),
    });
  }

  const editBattery = () => {
    Navigate(PATH.ADDBATTERY + `?bid=${bId}`, { state: { batteryDetail: BatteryDetail.current }, replace: true })
  }

  const breadcrumbPaths = [
    { idx: 1, name: "Inventory Management", link: PATH.INVENTORY },
    { idx: 1, name: "Batteries", link: PATH.BATTERIES },
    { idx: 1, name: BatteryDetail.current.bId, link: "", isCurrentPage: true },
  ];

  return (
    <div className='panel-main battery-detail-container'>
      <PopUpModal {...confirmDeleteBattery} />
      {showSuccessModal &&
        <SuccessPopupModal open={true} modalMsg={"Battery removed successfully"} closeBtn={"Okay"} modalSize={"lg"} onModalClose={() => { setShowSuccessModal(false); Navigate(PATH.BATTERIES) }} />}

      <div>
        <Breadcrumb paths={breadcrumbPaths} />
      </div>

      <div className="battery-detail-main">
        <div className='battery-detail-form-section'>
          <div className='vehicle-detail-header'>
            <Title>Battery Details</Title>
            {
              BatteryDetail.current.bId && !BatteryDetail.current.vid ? <div className='battery-detail-btn-wrapper'>
                <Button leftIcon={<PenIcon />} className='edit-battery' iconSpacing={1} variant={"outline"} onClick={editBattery}>Edit</Button>
                <Button leftIcon={<DeleteIcon />} className='delete-battery' iconSpacing={0} variant={"outline"} onClick={deleteBattery} />
              </div> : null
            }
          </div>
          <form className='battery-detail-form'>
            {
              formFields.map((formField, idx) => {
                return <Fragment key={idx}>
                  {
                    formField.type === FIELD_TYPE.TEXT && (
                      <TextInput
                        inputName={formField.name || ""}
                        placeholder={formField.placeholder}
                        className="battery-detail-text-field"
                        label={formField.label}
                        labelClassName="battery-detail-text-field-label"
                        value={formField.value}
                        readonly={true}
                      />
                    )
                  }
                </Fragment>
              })
            }
          </form>
        </div>
        <div className='battery-detail-timeline-section'>
          <Title>Battery Swapping</Title>
          <div className='timeline'>
            {
              timelineData.length ? <Timeline
                timelineData={timelineData}
                TimelineCardComp={TimelineCard}
              /> :
                <div className='not-found'>
                  No Data Found!
                </div>
            }
          </div>
        </div>
      </div>
    </div>
  )
};

const TimelineCard = ({ status, soc, vehReg, ts }) => {
  let formattedDate = `(${getLocalisedDate(ts, true)} at ${getLocalisedTime(ts, true)})`;

  return (
    <div className='timeline-card-cont'>
      <h1 className={status ? 'green' : 'red'} >{status ? "In Vehicle" : "Unplugged"}
        &nbsp;&nbsp;
        <span>{formattedDate}</span>
      </h1>
      {soc && <h4>SOC: {soc}</h4>}
      {(vehReg && status) ? <h4>Vehicle: {vehReg} </h4> : null}
    </div>
  )
};

export default BatteryDetail;